import { DexieRepository } from './repository.js';
import { ServerRepository } from './serverRepository.js';
import { db } from './db.js';
import { Domain } from './dataAccessTypes.js';
import {
  ENTITY_DOMAIN,
  ENTITY_PRIMARY_KEYS,
  isDexieEntity,
  getRepositoryKind as getRepositoryKindForEntity,
} from './dataAccessMap.js';
import type { EntityType } from './dataAccessMap.js';
import type { BaseEntity } from '../types.js';

export type RepositoryKind = 'dexie' | 'server';

const repoCache = new Map<EntityType, DexieRepository<BaseEntity> | ServerRepository<BaseEntity>>();

const getEnv = (key: string): string => {
  if (typeof process !== 'undefined' && process.env && typeof process.env[key] === 'string') {
    return process.env[key] as string;
  }
  if (typeof import.meta !== 'undefined' && (import.meta as any).env && (import.meta as any).env[key]) {
    return (import.meta as any).env[key];
  }
  return '';
};

const isAuthConfigured = () => !!(getEnv('GOOGLE_CLIENT_ID') || getEnv('VITE_GOOGLE_CLIENT_ID'));

const isDev = () =>
  (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') ||
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.MODE !== 'production');

const buildRepo = <T extends BaseEntity>(entity: EntityType): DexieRepository<T> | ServerRepository<T> => {
  const repositoryKind = getRepositoryKindForEntity(entity);
  const pkField = ENTITY_PRIMARY_KEYS[entity];

  const shouldUseServerRepo = repositoryKind === 'server';

  if (shouldUseServerRepo) {
    if (!isAuthConfigured() && isDev()) {
      console.warn(
        `[data-access] Using ServerRepository for ${entity} even though auth is not configured. Requests may fail until authentication is set up.`
      );
    }
    if (isDev() && isDexieEntity(entity)) {
      console.warn(
        `[data-access] Attempted to create server-first repository for ${entity} even though entity is Dexie-first.`
      );
    }
    return new ServerRepository<T>(entity, pkField);
  }

  if (!isDexieEntity(entity) && isDev()) {
    throw new Error(`[data-access] Server-first entity ${entity} attempted to use DexieRepository`);
  }
  return new DexieRepository<T>(db, entity as any, pkField);
};

export const getRepositoryForEntity = <T extends BaseEntity>(
  entity: EntityType
): DexieRepository<T> | ServerRepository<T> => {
  const cacheKey = `${db.name}:${entity}` as EntityType;
  if (!repoCache.has(cacheKey)) {
    repoCache.set(cacheKey, buildRepo<T>(entity));
  }
  return repoCache.get(cacheKey)! as DexieRepository<T> | ServerRepository<T>;
};

export const getDomainForEntity = (entity: EntityType): Domain => ENTITY_DOMAIN[entity];

export const getRepositoryKind = (entity: EntityType): RepositoryKind => getRepositoryKindForEntity(entity);

export const assertDexieUsage = (entity: EntityType) => {
  if (!isDexieEntity(entity) && isDev()) {
    throw new Error(`Server-first entity '${entity}' attempted to use DexieRepository.`);
  }
};

export type { EntityType };
